import React from 'react';
import { TeacherSubjectDto, SubjectDto } from '../../api/axios';
import { teacherSubjectsForForm } from '../../utils/teacherSubjectDisplay';
import { classLevelWord, formatClassLevelLabel } from '../../utils/classLevelLabel';
import type { SchoolCode } from '../../types';

interface ClassSelectorProps {
  teacherSubjects: TeacherSubjectDto[];
  allSubjects: SubjectDto[];
  selectedForm: number | null;
  selectedSubjectId: number | null;
  onFormChange: (form: number) => void;
  onSubjectChange: (subjectId: number) => void;
  loading?: boolean;
  school?: SchoolCode;
}

export const ClassSelector: React.FC<ClassSelectorProps> = ({
  teacherSubjects,
  allSubjects,
  selectedForm,
  selectedSubjectId,
  onFormChange,
  onSubjectChange,
  loading = false,
  school = 'KBA',
}) => {
  // Get only the forms where the teacher has assignments
  const teacherForms = Array.from(new Set(teacherSubjects.map(ts => ts.form))).sort((a, b) => a - b);

  // Group teacher subjects by form
  const teacherSubjectsByForm = teacherSubjects.reduce((acc, ts) => {
    if (!acc[ts.form]) {
      acc[ts.form] = [];
    }
    acc[ts.form].push(ts);
    return acc;
  }, {} as Record<number, TeacherSubjectDto[]>);

  // Use teacher assignment rows (with optional catalog fallback), not catalog-only intersection.
  const assignedSubjects = selectedForm
    ? teacherSubjectsForForm(teacherSubjects, selectedForm, allSubjects, school)
    : [];

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
        <div>Loading classes...</div>
      </div>
    );
  }

  if (teacherSubjects.length === 0) {
    return (
      <div
        style={{
          padding: 60,
          textAlign: 'center',
          background: '#fff',
          borderRadius: 12,
          border: '2px dashed #e0e0e0',
        }}
      >
        <div style={{ fontSize: 64, marginBottom: 16 }}>📚</div>
        <div style={{ fontSize: 20, fontWeight: 600, color: '#666', marginBottom: 8 }}>
          No Classes Assigned
        </div>
        <div style={{ fontSize: 14, color: '#999' }}>
          You haven't been assigned to any classes yet. Please contact your administrator.
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* Form Selection */}
        <div style={{ flex: 1 }}>
          <label
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 700,
              color: '#666',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Select {classLevelWord(school)}
          </label>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
              gap: 12,
            }}
          >
            {teacherForms.map((form) => {
              const isSelected = selectedForm === form;
              const subjectCount = teacherSubjectsByForm[form]?.length || 0;

              return (
                <button
                  key={form}
                  onClick={() => onFormChange(form)}
                  style={{
                    padding: '16px 20px',
                    background: isSelected
                      ? '#2563eb'
                      : '#fff',
                    color: isSelected ? '#fff' : '#333',
                    border: isSelected ? 'none' : '2px solid #007bff',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: isSelected ? 700 : 600,
                    fontSize: 16,
                    transition: 'all 0.2s',
                    boxShadow: isSelected
                      ? '0 4px 12px rgba(0,123,255,0.3)'
                      : '0 3px 8px rgba(0,123,255,0.15)',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#0056b3';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,123,255,0.25)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = '#007bff';
                      e.currentTarget.style.boxShadow = '0 3px 8px rgba(0,123,255,0.15)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
                    {formatClassLevelLabel(school, form)}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      opacity: 0.9,
                      fontWeight: 600,
                    }}
                  >
                    {subjectCount} {subjectCount === 1 ? 'Subject' : 'Subjects'}
                  </div>
                  {!isSelected && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        width: 10,
                        height: 10,
                        background: '#28a745',
                        borderRadius: '50%',
                        border: '2px solid #fff',
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Subject Selection - Only Show Assigned Subjects */}
      {selectedForm && (
        <div style={{ marginTop: 24 }}>
          {assignedSubjects.length > 0 ? (
            <>
              <label
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#666',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Select Subject for {formatClassLevelLabel(school, selectedForm)}
              </label>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 12,
                }}
              >
                {assignedSubjects.map((subject) => {
                  const isSelected = selectedSubjectId === subject.id;

                  return (
                    <button
                      key={subject.id}
                      onClick={() => onSubjectChange(subject.id)}
                      style={{
                        padding: '14px 18px',
                        background: isSelected 
                          ? '#059669'
                          : '#fff',
                        color: isSelected ? '#fff' : '#333',
                        border: isSelected ? 'none' : '2px solid #28a745',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 15,
                        textAlign: 'left',
                        transition: 'all 0.2s',
                        boxShadow: isSelected
                          ? '0 4px 12px rgba(40,167,69,0.4)'
                          : '0 3px 8px rgba(40,167,69,0.15)',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = '#f0f9f4';
                          e.currentTarget.style.borderColor = '#1e7e34';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(40,167,69,0.25)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.background = '#fff';
                          e.currentTarget.style.borderColor = '#28a745';
                          e.currentTarget.style.boxShadow = '0 3px 8px rgba(40,167,69,0.15)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      <div style={{ fontSize: 16, fontWeight: 700 }}>
                        {subject.name}
                      </div>
                      {!isSelected && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 10,
                            height: 10,
                            background: '#28a745',
                            borderRadius: '50%',
                            border: '2px solid #fff',
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                color: '#999',
                background: '#f9f9f9',
                borderRadius: 8,
                border: '2px dashed #e0e0e0',
              }}
            >
              <div style={{ fontSize: 48, marginBottom: 12 }}>📚</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                No Subjects Assigned
              </div>
              <div style={{ fontSize: 14 }}>
                You have no subjects assigned for {formatClassLevelLabel(school, selectedForm)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
